import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Separator } from '@/components/ui/separator';
import { Calculator, TrendingUp, MessageCircle, BadgeDollarSign, BadgeEuro, BadgePoundSterling, Coins, Save, AlertTriangle, ChevronUp, Eraser } from 'lucide-react';
import { useCommunity } from '@/contexts/CommunityContext';
import { useBusiness } from '@/contexts/BusinessContext';
import { 
  useCreateOrUpdateQuote, 
  useManageQuoteItems,
  CreateQuoteData
} from '@/hooks/useJobProposals';
import { QuoteItemsManager, QuoteItemFormData } from './QuoteItemsManager';

interface ExistingQuote {
  id: string;
  price_type: 'fixed' | 'estimate' | 'range';
  status: 'partial' | 'complete';
  fixed_price: number | null;
  estimated_price: number | null;
  min_price: number | null;
  max_price: number | null;
  notes: string | null;
  quote_items?: Array<{
    id: string;
    label: string;
    quantity: number;
    unit_label: string;
    unit_price: number;
    line_total: number;
    display_order: number;
  }> | null;
}

interface QuoteFormProps {
  proposalId: string;
  existingQuote?: ExistingQuote | null;
  formData: {
    price_type: 'fixed' | 'estimate' | 'range' | 'no_quote' | '';
    is_partial: boolean;
    fixed_price: string;
    estimated_price: string;
    min_price: string;
    max_price: string;
    notes: string;
  };
  quoteItems: QuoteItemFormData[];
  showBreakdown: boolean;
  onChange?: (quoteData: any, quoteItems: QuoteItemFormData[]) => void;
  onFormDataChange: (formData: any) => void;
  onQuoteItemsChange: (items: QuoteItemFormData[]) => void;
  onShowBreakdownChange: (show: boolean) => void;
  onCollapse?: () => void;
  onReset?: () => void;
}

export const QuoteForm: React.FC<QuoteFormProps> = ({
  proposalId,
  existingQuote,
  formData,
  quoteItems,
  showBreakdown,
  onChange,
  onFormDataChange,
  onQuoteItemsChange,
  onShowBreakdownChange,
  onCollapse,
  onReset
}) => {
  const { getParameter } = useCommunity();
  const { userBusinessId } = useBusiness();
  const currency = getParameter<string>('currency') || '$';
  
  // Get appropriate currency icon based on community currency
  const getCurrencyIcon = () => {
    switch (currency) {
      case '£':
        return BadgePoundSterling;
      case '€':
      case 'EUR':
        return BadgeEuro;
      case '$':
      case 'USD':
        return BadgeDollarSign;
      default:
        return Coins; // Generic fallback
    }
  };
  
  const CurrencyIcon = getCurrencyIcon();
  
  const [saveValidationError, setSaveValidationError] = useState<string>('');

  // Clear validation error when form data changes
  useEffect(() => {
    setSaveValidationError('');
  }, [formData]);


  const getPriceTypeIcon = (type: string) => {
    switch (type) {
      case 'fixed':
        return CurrencyIcon;
      case 'estimate':
        return Calculator;
      case 'range':
        return TrendingUp;
      default:
        return CurrencyIcon;
    }
  };

  const renderPriceInputs = () => {
    switch (formData.price_type) {
      case 'fixed':
        return (
          <div>
            <Label className="text-sm font-medium">Fixed Price Amount ({currency})</Label>
            <Input
              type="number"
              min="0.01"
              step="0.01"
              placeholder="0.01"
              value={formData.fixed_price}
              onChange={(e) => onFormDataChange({ ...formData, fixed_price: e.target.value })}
              onWheel={(e) => e.currentTarget.blur()}
              className="mt-1"
            />
            <p className="text-xs text-gray-600 mt-1">
              This is the exact amount you will charge for this service.
            </p>
          </div>
        );

      case 'estimate':
        return (
          <div>
            <Label className="text-sm font-medium">Estimated Price ({currency})</Label>
            <Input
              type="number"
              min="0.01"
              step="0.01"
              placeholder="0.01"
              value={formData.estimated_price}
              onChange={(e) => onFormDataChange({ ...formData, estimated_price: e.target.value })}
              onWheel={(e) => e.currentTarget.blur()}
              className="mt-1"
            />
            <p className="text-xs text-gray-600 mt-1">
              This is your best estimate. Final price may vary based on actual requirements.
            </p>
          </div>
        );

      case 'range':
        return (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-medium">Min. Price ({currency})</Label>
              <Input
                type="number"
                min="0.01"
                step="0.01"
                placeholder="0.01"
                value={formData.min_price}
                onChange={(e) => {
                  const newMinPrice = e.target.value;
                  // Clear max price if min price is being cleared
                  const updatedFormData = {
                    ...formData,
                    min_price: newMinPrice,
                    max_price: newMinPrice ? formData.max_price : ''
                  };
                  onFormDataChange(updatedFormData);
                }}
                onWheel={(e) => e.currentTarget.blur()}
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-sm font-medium">Max. Price ({currency})</Label>
              <Input
                type="number"
                min="0.01"
                step="0.01"
                placeholder="0.01"
                value={formData.max_price}
                onChange={(e) => onFormDataChange({ ...formData, max_price: e.target.value })}
                onWheel={(e) => e.currentTarget.blur()}
                className="mt-1"
                disabled={!formData.min_price}
              />
              {!formData.min_price && (
                <p className="text-xs text-gray-500 mt-1">
                  Set minimum price first
                </p>
              )}
            </div>
            <div className="col-span-2">
              <p className="text-xs text-gray-600">
                Provide a realistic price range. The actual quote will depend on specific requirements.
              </p>
              {/* Range validation */}
              {formData.min_price && formData.max_price && parseFloat(formData.min_price) > parseFloat(formData.max_price) && (
                <div className="mt-2 flex items-center gap-1 text-xs text-red-600 font-medium">
                  <AlertTriangle className="w-3 h-3" />
                  Maximum price cannot be less than minimum price
                </div>
              )}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
          <CardTitle className="text-base">Quote Details</CardTitle>
          {onReset && (formData.price_type || quoteItems.length > 0) && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onReset}
              className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
              title="Clear quote data"
            >
              <Eraser className="w-4 h-4" />
            </Button>
          )}
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div>
              <Select
                value={formData.price_type}
                onValueChange={(value) => {
                  const newPriceType = value as 'fixed' | 'estimate' | 'range' | 'no_quote';
                  // Reset irrelevant price fields when switching quote types
                  const resetFormData = {
                    ...formData,
                    price_type: newPriceType,
                    // Clear all price fields, user will enter relevant ones
                    fixed_price: '',
                    estimated_price: '',
                    min_price: '',
                    max_price: ''
                  };
                  onFormDataChange(resetFormData);
                }}
              >
                <SelectTrigger className="text-left">
                  <SelectValue placeholder="Select quote type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="fixed">
                    <div className="flex items-center gap-2">
                      <CurrencyIcon className="w-4 h-4 text-green-600" />
                      <span className="font-medium">Fixed Price</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="estimate">
                    <div className="flex items-center gap-2">
                      <Calculator className="w-4 h-4 text-blue-600" />
                      <span className="font-medium">Estimate</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="range">
                    <div className="flex items-center gap-2">
                      <TrendingUp className="w-4 h-4 text-orange-600" />
                      <span className="font-medium">Price Range</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="no_quote">
                    <div className="flex items-center gap-2">
                      <MessageCircle className="w-4 h-4 text-gray-600" />
                      <span className="font-medium">No Quote</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
              
              {/* Dynamic description based on selected option */}
              {formData.price_type && (
                <div className="text-xs text-muted-foreground p-2 bg-gray-50 rounded-md mt-2">
                  {formData.price_type === 'fixed' && "Exact amount for the complete service"}
                  {formData.price_type === 'estimate' && "Best guess based on typical requirements"}
                  {formData.price_type === 'range' && "Minimum to maximum expected cost"}
                  {formData.price_type === 'no_quote' && "Respond without providing pricing information"}
                </div>
              )}
            </div>

            {/* Helper text when no quote type is selected */}
            {!formData.price_type && (
              <div className="text-center py-6 text-muted-foreground">
                <p className="text-sm">👆 Choose how you'd like to price this service to continue</p>
              </div>
            )}

            {/* Show price inputs only when quote type is selected (but not no_quote) */}
            {formData.price_type && formData.price_type !== 'no_quote' && (
              <div className="space-y-4">
                <div>
                  {renderPriceInputs()}
                </div>

                {/* Quote breakdown inside pricing details */}
                <div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => onShowBreakdownChange(!showBreakdown)}
                    className={`text-sm p-0 h-auto font-medium active:bg-transparent focus:bg-transparent ${
                      showBreakdown 
                        ? 'text-muted-foreground hover:text-foreground'
                        : quoteItems.length > 0
                          ? 'text-green-700 hover:text-green-800'
                          : 'text-foreground hover:text-foreground'
                    }`}
                  >
                    {showBreakdown ? 'Hide quote breakdown' : quoteItems.length > 0 ? 'Display quote breakdown' : 'Add quote breakdown'}
                    {!showBreakdown && quoteItems.length > 0 && (
                      <span className="ml-2 inline-flex items-center justify-center w-5 h-5 text-xs font-medium text-white bg-green-600 rounded-full">
                        {quoteItems.length}
                      </span>
                    )}
                  </Button>
                  {showBreakdown && (
                    <div className="mt-3">
                      <QuoteItemsManager
                        items={quoteItems}
                        onChange={(items) => onQuoteItemsChange(items)}
                        priceType={formData.price_type}
                        businessId={userBusinessId || undefined}
                        quoteLimits={{
                          fixed_price: formData.price_type === 'fixed' ? parseFloat(formData.fixed_price) || 0 : undefined,
                          estimated_price: formData.price_type === 'estimate' ? parseFloat(formData.estimated_price) || 0 : undefined,
                          min_price: formData.price_type === 'range' ? parseFloat(formData.min_price) || 0 : undefined,
                          max_price: formData.price_type === 'range' ? parseFloat(formData.max_price) || 0 : undefined,
                        }}
                      />
                    </div>
                  )}
                </div>

                <Separator />

                {/* Quote completeness checkbox */}
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="is-partial"
                    checked={formData.is_partial}
                    onCheckedChange={(checked) => onFormDataChange({ ...formData, is_partial: checked as boolean })}
                  />
                  <Label htmlFor="is-partial" className="text-xs font-normal cursor-pointer">
                    Unable to provide a complete quote? Mark as incomplete to let the customer know additional costs may apply
                  </Label>
                </div>
                {formData.is_partial && (
                  <p className="text-xs text-orange-600 ml-6">
                    ⚠️ Mark this if you need more information or additional costs may apply based on specific requirements.
                  </p>
                )}

                {/* Notes */}
                <div>
                  <Label className="text-sm font-medium">Notes (Optional)</Label>
                  <Textarea
                    placeholder="Add any details about your pricing, timeline, conditions, or questions..."
                    value={formData.notes}
                    onChange={(e) => onFormDataChange({ ...formData, notes: e.target.value })}
                    className="mt-1 min-h-[80px]"
                  />
                </div>

              </div>
            )}
          </div>
        </CardContent>
        
        {/* Collapse button at bottom */}
        {onCollapse && (
          <div className="px-6 pb-4 flex justify-center">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onCollapse}
              className="text-muted-foreground hover:text-foreground"
            >
              <ChevronUp className="w-4 h-4 mr-2" />
              Collapse quote details
            </Button>
          </div>
        )}
      </Card>
    </div>
  );
};